import { Cards, Box, Header, SpaceBetween, Button } from '@cloudscape-design/components';
import { templateRegistry } from '../templates/registry';

interface TemplateGalleryProps {
  /** Receives the registry key (e.g. 'queue-features-announcements') not just configType */
  onSelectTemplate: (templateKey: string) => void;
  onCreateFromScratch: () => void;
  onImportJson: () => void;
}

/**
 * Template gallery displaying available configType templates as cards.
 * Groups sub-type templates (e.g. Queue Features) under category headers.
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.7
 */
export function TemplateGallery({
  onSelectTemplate,
  onCreateFromScratch,
  onImportJson,
}: TemplateGalleryProps) {
  // Build items with their registry key so we can pass it back on selection
  const items = Array.from(templateRegistry.entries())
    .filter(([key]) => key !== 'queue-features') // Exclude generic fallback from gallery
    .filter(([, t]) => t.configType !== 'custom')
    .map(([key, t]) => ({ ...t, _registryKey: key }));

  return (
    <SpaceBetween size="l">
      <Header
        variant="h2"
        description="Choose a template to get started, create from scratch, or import JSON."
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onImportJson}>Import JSON</Button>
            <Button onClick={onCreateFromScratch}>Create from scratch</Button>
          </SpaceBetween>
        }
      >
        Create Configuration
      </Header>

      <Cards
        items={items}
        cardDefinition={{
          header: (item) => (
            <Box fontWeight="bold" fontSize="heading-s">
              {item.displayName}
            </Box>
          ),
          sections: [
            {
              id: 'category',
              content: (item) =>
                item.templateCategory ? (
                  <Box variant="small" color="text-body-secondary">
                    {item.templateCategory}
                  </Box>
                ) : null,
            },
            {
              id: 'description',
              content: (item) => <Box variant="p">{item.description}</Box>,
            },
            {
              id: 'fields',
              header: 'Fields',
              content: (item) => (
                <Box variant="small" color="text-body-secondary">
                  {item.payloadSchema.length} configurable fields
                </Box>
              ),
            },
            {
              id: 'action',
              content: (item) => (
                <Button variant="primary" onClick={() => onSelectTemplate(item._registryKey)}>
                  Use template
                </Button>
              ),
            },
          ],
        }}
        cardsPerRow={[{ cards: 1 }, { minWidth: 400, cards: 2 }, { minWidth: 800, cards: 3 }]}
        empty={
          <Box textAlign="center">No templates available.</Box>
        }
      />
    </SpaceBetween>
  );
}
